<template>
  <dialog ref="dialogRef" class="modal" @close="onClose">
    <div class="modal-content">
      <!-- Header -->
      <header class="modal-header">
        <h2 class="modal-title">{{ title }}</h2>
        <Button 
          variant="icon" 
          size="small" 
          @click="closeModal"
          class="modal-close"
        >
          <Icon name="x" :size="16" />
        </Button>
      </header>
      
      <!-- Body -->
      <main class="modal-body">
        <slot />
      </main>
      
      <!-- Footer -->
      <footer v-if="$slots.footer || showDefaultActions" class="modal-footer">
        <slot name="footer">
          <div v-if="showDefaultActions" class="modal-actions">
            <Button 
              variant="secondary" 
              @click="onCancel"
            >
              {{ cancelText }}
            </Button>
            <Button 
              variant="primary" 
              @click="onConfirm"
              :disabled="!confirmEnabled"
            >
              {{ confirmText }}
            </Button>
          </div>
        </slot>
      </footer>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import Button from './Button.vue'
import Icon from './Icon.vue'

interface Props {
  title: string
  showDefaultActions?: boolean
  confirmText?: string
  cancelText?: string
  confirmEnabled?: boolean
  trapFocus?: boolean
}

export type ModalProps = Props

interface Emits {
  (e: 'close'): void
  (e: 'confirm'): void
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  showDefaultActions: true,
  confirmText: 'OK',
  cancelText: 'Cancel',
  confirmEnabled: true,
  trapFocus: true
})

const emit = defineEmits<Emits>()

const dialogRef = ref<HTMLDialogElement>()

const showModal = async () => {
  await nextTick()
  dialogRef.value?.showModal()
  
  if (props.trapFocus) {
    // Focus first focusable element
    const focusableElements = dialogRef.value?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusableElements && focusableElements.length > 0) {
      (focusableElements[0] as HTMLElement).focus()
    }
  }
}

const closeModal = () => {
  dialogRef.value?.close()
}

const onClose = () => {
  emit('close')
}

const onConfirm = () => {
  emit('confirm')
}

const onCancel = () => {
  emit('cancel')
  closeModal()
}

// Handle Escape key
const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    onCancel()
  }
}

// Handle backdrop click
const handleBackdropClick = (event: MouseEvent) => {
  if (event.target === dialogRef.value) {
    onCancel()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
  dialogRef.value?.addEventListener('click', handleBackdropClick)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
  dialogRef.value?.removeEventListener('click', handleBackdropClick)
})

defineExpose({
  showModal,
  closeModal
})
</script>

<style scoped lang="scss">
.modal {
  padding: 0;
  margin: auto;
  border: none;
  border-radius: 6px;
  background: var(--vscode-dropdown-background);
  color: var(--vscode-dropdown-foreground);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: min(90vw, 600px);
  max-height: min(90vh, 800px);
  overflow: hidden;
  
  &::backdrop {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
}

.modal-content {
  display: flex;
  flex-direction: column;
  min-height: 200px;
  max-height: min(90vh, 800px);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--vscode-widget-border);
  background: var(--vscode-editor-background);
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--vscode-foreground);
}

.modal-close {
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
}

.modal-body {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  
  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--vscode-scrollbarSlider-background);
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--vscode-scrollbarSlider-hoverBackground);
    border-radius: 4px;
  }
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid var(--vscode-widget-border);
  background: var(--vscode-editor-background);
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

// Animation
.modal {
  animation: modalEnter 0.2s ease-out;
}

@keyframes modalEnter {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
